import { skipToken, useQuery } from '@tanstack/react-query';
import { useEffect, useEffectEvent, useRef, useState } from 'react';

import { useEvidenceRowSelectionStore } from '#package-store/package-family-editor/package-evidence-panel/evidence-row-selection-store.js';
import { useTRPC } from '#src/trpc.js';
import { firstFollowableLocator } from './first-followable-locator.js';

import type { RefObject } from 'react';
import type { EvidenceRowPick } from '#package-store/package-family-editor/package-evidence-panel/evidence-row-selection-store.js';
import type { ShowInDatasheet } from '#package-store/package-family-editor/package-evidence-panel/show-in-datasheet-context.js';
import type { DatasheetPdfFrameHandle } from './datasheet-pdf-frame.js';
import type { DatasheetPopoverBoundingBox, DatasheetPopoverTarget } from './datasheet-popover-store.js';

/** What a datasheet window draws over its PDF, and how to move it. */
export interface DatasheetWindowFollow {
	/** The bounding boxes drawn over the PDF; empty for none. */
	boundingBoxes: DatasheetPopoverBoundingBox[];
	/** The package whose evidence the window shows, or undefined when it shows none. */
	evidencePackageId: string | undefined;
	/** Shows a target's page and bounding boxes, holding them there until the row pick changes. */
	showInDatasheet: ShowInDatasheet;
}

/** What set the window's current view: the pick it was set under, and the locator followed to it, if any. */
interface DatasheetViewOrigin {
	/** The row pick when the view was set. */
	pick: EvidenceRowPick | null;
	/** Address of the locator the window followed to the view, or null when the viewer chose the view. */
	followedAddress: string | null;
}

const NO_BOUNDING_BOXES: DatasheetPopoverBoundingBox[] = [];

/**
 * Keeps a datasheet window on its target's page and bounding boxes, and on any other its viewer shows within it. A
 * window opened from a package's evidence also follows the viewer's pick of that package's rows while following is
 * on and its evidence is showing: a new pick moves it to the pick's first followable locator, if any, but a view the
 * viewer chose (the one the window opened on, or one shown within it) holds until the pick changes. Clearing the pick
 * takes the bounding boxes off the page shown. A new target reopens the window on that target's page and boxes.
 *
 * @param target - The request that opened the window.
 * @param showsEvidence - Whether the window is showing its evidence; it follows the pick only while it is.
 * @param pdfFrameRef - The window's PDF frame, moved to each page the window goes to.
 * @returns The bounding boxes drawn, the package whose evidence the window shows, and the function that moves the
 * window.
 */
export function useDatasheetWindowFollow(
	target: DatasheetPopoverTarget,
	showsEvidence: boolean,
	pdfFrameRef: RefObject<DatasheetPdfFrameHandle | null>,
): DatasheetWindowFollow {
	const trpc = useTRPC();
	const evidencePackageId = target.evidence?.packageId;
	const pick = useEvidenceRowSelectionStore((state) => state.selection);
	const followSelection = useEvidenceRowSelectionStore((state) => state.followSelection);
	const followedPick =
		evidencePackageId !== undefined && showsEvidence && followSelection && pick?.packageId === evidencePackageId ?
			pick
		:	null;

	const rowEvidenceQuery = useQuery(
		trpc.packageEvidence.rowEvidence.queryOptions(
			followedPick === null ? skipToken : { packageId: followedPick.packageId, selections: followedPick.selections },
			{ throwOnError: false },
		),
	);
	const followed =
		followedPick === null || rowEvidenceQuery.data === undefined ?
			null
		:	firstFollowableLocator(rowEvidenceQuery.data.locators);
	const followedAddress = followed?.address ?? null;

	const [boundingBoxes, setBoundingBoxes] = useState(() => target.initialBoundingBoxes ?? NO_BOUNDING_BOXES);
	const viewOriginRef = useRef<DatasheetViewOrigin>({ pick, followedAddress: null });
	const openedTargetRef = useRef(target);

	const showChosen = (chosen: DatasheetPopoverTarget) => {
		viewOriginRef.current = { pick, followedAddress: null };
		setBoundingBoxes(chosen.initialBoundingBoxes ?? NO_BOUNDING_BOXES);
		pdfFrameRef.current?.goToPage(chosen.initialPageNumber ?? 1);
	};

	const reopen = useEffectEvent(showChosen);
	useEffect(() => {
		if (openedTargetRef.current === target) {
			return;
		}
		openedTargetRef.current = target;
		reopen(target);
	}, [target]);

	const follow = useEffectEvent((currentPick: EvidenceRowPick | null) => {
		const origin = viewOriginRef.current;
		if (
			evidencePackageId === undefined ||
			(origin.pick === currentPick && (origin.followedAddress === null || origin.followedAddress === followedAddress))
		) {
			return;
		}
		if (currentPick === null) {
			viewOriginRef.current = { pick: null, followedAddress: null };
			setBoundingBoxes(NO_BOUNDING_BOXES);
			return;
		}
		if (followed === null) {
			return;
		}
		viewOriginRef.current = { pick: currentPick, followedAddress: followed.address };
		setBoundingBoxes(followed.boundingBoxes);
		pdfFrameRef.current?.goToPage(followed.pageNumber);
	});
	useEffect(() => {
		follow(pick);
	}, [pick, followedAddress]);

	return { boundingBoxes, evidencePackageId, showInDatasheet: showChosen };
}
